import React, { useState } from "react";
import { i18n } from "../../translate/i18n";
import MutedIcon from "../MuteDialog/MutedIcon";
import {
    Avatar,
    Dialog,
    DialogContent,
    Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { getTokens, brandAlpha, SEMANTIC } from "../../styles/tokens";

const useStyles = makeStyles((theme) => {
    const t = getTokens(theme.palette.type, theme.palette.primary.main);
    const isDark = theme.palette.type === "dark";
    const badgeBase = {
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        height: 18,
        padding: "0 7px",
        borderRadius: 999,
        fontSize: 10.5,
        fontWeight: 500,
        lineHeight: "18px",
        whiteSpace: "nowrap",
        maxWidth: 160,
        overflow: "hidden",
        textOverflow: "ellipsis",
    };
    return {
        root: {
            display: "flex",
            alignItems: "center",
            gap: 9,
            minWidth: 0,
            flex: 1,
            cursor: "pointer",
            padding: "3px 7px",
            minHeight: 44,
            maxWidth: 620,
            borderRadius: t.radius.sm,
            transition: "background-color 150ms ease",
            "&:hover": {
                backgroundColor: t.muted,
            },
            [theme.breakpoints.down("md")]: { maxWidth: 380 },
            [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
        },
        avatar: {
            width: 36,
            height: 36,
            flexShrink: 0,
            backgroundColor: t.tint.medium,
            color: t.brand,
            fontSize: 14,
            fontWeight: 700,
            cursor: "pointer",
            transition: "opacity 150ms ease",
            "&:hover": {
                opacity: 0.85,
            },
        },
        texts: {
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            minWidth: 0,
            gap: 2,
        },
        mutedIcon: {
            color: t.textMuted,
            fontSize: 16,
        },
        nameRow: {
            display: "flex",
            alignItems: "center",
            gap: 6,
            minWidth: 0,
        },
        name: {
            fontSize: 14,
            fontWeight: 700,
            color: t.text,
            lineHeight: 1.3,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
        },
        ticketId: {
            display: "inline-flex",
            alignItems: "center",
            height: 18,
            padding: "0 6px",
            borderRadius: 999,
            border: `1px solid ${t.line}`,
            backgroundColor: t.muted,
            fontSize: 10,
            fontWeight: 600,
            color: t.textMuted,
            fontVariantNumeric: "tabular-nums",
            flexShrink: 0,
        },
        // estado do contato, como no WhatsApp: "digitando…" em verde, "online" discreto
        presenceTyping: {
            flex: "none",
            fontSize: 12.5,
            fontWeight: 600,
            color: isDark ? "#00a884" : "#008069",
        },
        presenceOnline: {
            flex: "none",
            fontSize: 12.5,
            color: isDark ? "rgba(233,237,239,0.6)" : "#667781",
        },
        metaRow: {
            display: "flex",
            alignItems: "center",
            gap: 5,
            minWidth: 0,
            flexWrap: "nowrap",
            overflow: "hidden",
        },
        badge: badgeBase,
        badgeStatusOpen: {
            ...badgeBase,
            backgroundColor: t.accentTint.soft,
            color: isDark ? t.accent : t.accentHover,
        },
        badgeStatusPending: {
            ...badgeBase,
            backgroundColor: brandAlpha(SEMANTIC.warning, isDark ? 0.22 : 0.12),
            color: isDark ? "#fbbf24" : SEMANTIC.warning,
        },
        badgeStatusClosed: {
            ...badgeBase,
            backgroundColor: isDark ? "rgba(148,163,184,0.16)" : "rgba(100,116,139,0.10)",
            color: t.textSecondary,
        },
        badgeStatusGroup: {
            ...badgeBase,
            backgroundColor: brandAlpha(SEMANTIC.info, isDark ? 0.22 : 0.10),
            color: isDark ? "#93c5fd" : SEMANTIC.info,
        },
        badgeMuted: {
            ...badgeBase,
            backgroundColor: t.muted,
            border: `1px solid ${t.line}`,
            color: t.textSecondary,
        },
        dot: {
            width: 6,
            height: 6,
            borderRadius: "50%",
            flexShrink: 0,
        },
        meta: {
            fontSize: 12,
            color: t.textSecondary,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
        },
        // Modal da imagem
        imageModal: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
        },
        imageModalContent: {
            outline: "none",
            maxWidth: "90vw",
            maxHeight: "90vh",
        },
        expandedImage: {
            width: "100%",
            height: "auto",
            maxWidth: "500px",
            borderRadius: t.radius.sm,
        },
    };
});

const STATUS_LABEL_KEY = {
    open: "tickets.search.filterConectionsOptions.open",
    closed: "tickets.search.filterConectionsOptions.closed",
    pending: "tickets.search.filterConectionsOptions.pending",
    group: "ticketsList.groupingHeader",
};

// TODO i18n
const PRESENCE_LABEL = {
    composing: "digitando…",
    recording: "gravando áudio…",
    online: "online",
};

const TicketInfo = ({ contact, ticket, presence, onClick }) => {
    const classes = useStyles();
    const [imageModalOpen, setImageModalOpen] = useState(false);

    const handleImageClick = (e) => {
        e.stopPropagation();
        if (contact?.urlPicture) {
            setImageModalOpen(true);
        }
    };

    const handleImageModalClose = () => {
        setImageModalOpen(false);
    };

    const status = ticket?.status;
    const statusClass =
        status === "open"
            ? classes.badgeStatusOpen
            : status === "pending"
            ? classes.badgeStatusPending
            : status === "group"
            ? classes.badgeStatusGroup
            : classes.badgeStatusClosed;
    const statusLabel = STATUS_LABEL_KEY[status]
        ? i18n.t(STATUS_LABEL_KEY[status])
        : status;

    const queue = ticket?.queue;
    const connectionName = ticket?.whatsapp?.name;
    const walletName =
        contact?.contactWallets && contact.contactWallets.length > 0
            ? contact.contactWallets[0].wallet?.name
            : null;
    const contactName = contact?.name || "(sem contato)";
    // charAt(0) quebra nomes que começam por emoji (retorna apenas metade do
    // surrogate pair). Array.from mantém o caractere completo no avatar.
    const contactInitial = Array.from(String(contactName).trim())[0] || "?";

    const handleRootKeyDown = (event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onClick?.(event);
        }
    };

    return (
        <React.Fragment>
            <div
                className={classes.root}
                onClick={onClick}
                onKeyDown={handleRootKeyDown}
                role="button"
                tabIndex={0}
                aria-label={`Abrir detalhes de ${contactName}`}
            >
                <Avatar
                    src={contact?.urlPicture}
                    alt="contact_image"
                    className={classes.avatar}
                    onClick={handleImageClick}
                >
                    {contactInitial.toUpperCase()}
                </Avatar>
                <div className={classes.texts}>
                    <div className={classes.nameRow}>
                        <span className={classes.name} title={contactName}>
                            {contactName}
                        </span>
                        <span className={classes.ticketId}>#{ticket?.id}</span>
                        <MutedIcon
                            contactId={ticket?.contactId || contact?.id}
                            className={classes.mutedIcon}
                        />
                    </div>
                    <div className={classes.metaRow}>
                        {PRESENCE_LABEL[presence] && (
                            <span
                                className={presence === "online" ? classes.presenceOnline : classes.presenceTyping}
                                aria-live="polite"
                            >
                                {PRESENCE_LABEL[presence]}
                            </span>
                        )}
                        {status && (
                            <span className={statusClass}>{statusLabel}</span>
                        )}
                        {queue?.name && (
                            <Tooltip title={queue.name}>
                                <span className={classes.badgeMuted}>
                                    <span
                                        className={classes.dot}
                                        style={{ backgroundColor: queue.color || "currentColor" }}
                                    />
                                    {queue.name}
                                </span>
                            </Tooltip>
                        )}
                        {connectionName && (
                            <Tooltip title={connectionName}>
                                <span className={classes.badgeMuted}>{connectionName}</span>
                            </Tooltip>
                        )}
                        {ticket?.user && (
                            <span className={classes.meta}>
                                {i18n.t("messagesList.header.assignedTo")} {ticket.user.name}
                            </span>
                        )}
                        {walletName && (
                            <span className={classes.meta}>
                                • {i18n.t("wallets.wallet")}: {walletName}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Modal da Imagem */}
            <Dialog
                open={imageModalOpen}
                onClose={handleImageModalClose}
                className={classes.imageModal}
                maxWidth="md"
                fullWidth
            >
                <DialogContent className={classes.imageModalContent}>
                    <img
                        src={contact?.urlPicture}
                        alt={contact?.name || "Foto do contato"}
                        className={classes.expandedImage}
                    />
                </DialogContent>
            </Dialog>
        </React.Fragment>
    );
};

export default TicketInfo;
